import React, { useId, useRef, useState } from 'react';
import RecoverableImage from '../../../features/images/RecoverableImage';
import {
  FiEdit2,
  FiCamera,
  FiCheck,
  FiSettings,
  FiShare,
  FiArrowLeft,
  FiMoreHorizontal,
  FiUserPlus,
  FiUserCheck,
} from 'react-icons/fi';
import { ProfileMark } from '../../../components/content/ProfileIdentity';
import { useColorScheme } from '../../../contexts/ColorSchemeContext';
import { useWebLocale } from '../../../contexts/WebLocaleContext';
import baseline from '../../../app-ui/baseline';
import './MyProfileHero.css';
import { useContinuousHero } from '../../../components/layout/ContinuousHero';

export function myProfileMark(profile) {
  const type =
    profile.profileMarkType ??
    profile.profileMark?.type ??
    (profile.featuredBadge ? 'ACTIVITY' : 'NONE');
  if (type === 'ACTIVITY')
    return profile.featuredBadge
      ? { type, activityBadge: profile.featuredBadge }
      : profile.profileMark?.type === type
        ? profile.profileMark
        : { type: 'NONE' };
  if (type === 'SPONSORSHIP')
    return profile.sponsorshipMark?.isSupporter
      ? { type, sponsorshipLevel: profile.sponsorshipMark.currentStage?.level ?? 1 }
      : profile.profileMark?.type === type
        ? profile.profileMark
        : { type: 'NONE' };
  return { type: 'NONE' };
}

export default function MyProfileHero({
  profile,
  onSettings,
  onEdit,
  onShare,
  onFindUsers,
  onFollowers,
  onFollowing,
  onBack,
  onSafety,
  primaryAction,
  publicProfile = false,
  shareDisabled = false,
  editing = false,
  busy = false,
  onSave,
  onProfileImage,
  onBackgroundImage,
}) {
  const { locale } = useWebLocale(),
    { resolvedMode } = useColorScheme();
  const en = locale === 'en',
    dark = resolvedMode === 'dark';
  const colors = baseline.tokens[dark ? 'darkColors' : 'lightColors'];
  const [failedBackground, setFailedBackground] = useState(null);
  const image = profile.profileImageUrl,
    background = profile.profileBackgroundImageUrl;
  const hasBackground = Boolean(background && failedBackground !== background);
  const heroRef = useRef(null);
  useContinuousHero(heroRef, hasBackground);
  const id = useId().replace(/:/g, '');
  const nickname = profile.nickname?.trim() || (en ? 'Member' : '회원');
  const palette = profile.profileMark?.nicknameColor;
  const nicknameColor =
    palette?.key === 'VINYL' ? (dark ? '#FF79AD' : '#B8326A') : palette?.[dark ? 'dark' : 'light'];
  const emptyBio = publicProfile
    ? en
      ? 'No bio yet.'
      : '아직 자기소개가 없어요.'
    : en
      ? 'Add a bio to share your music taste.'
      : '자기소개를 추가해 나의 음악 취향을 알려주세요.';
  return (
    <section
      ref={heroRef}
      className={`pp-my-hero${hasBackground ? ' has-background' : ''}${editing ? ' is-editing' : ''}${hasBackground ? ' pp-continuous-hero' : ''}`}
      aria-label={
        publicProfile
          ? en
            ? `${nickname}’s profile`
            : `${nickname}님의 프로필`
          : en
            ? 'My profile'
            : '내 프로필'
      }
      style={{
        '--my-bg': colors.background,
        '--my-text': colors.text,
        '--my-secondary': colors.textSecondary,
        '--my-muted': colors.textMuted,
        '--my-surface': colors.surfaceElevated,
        '--my-border': colors.dividerStrong,
        '--my-avatar': colors.surfaceMuted,
      }}
    >
      <div className="pp-my-hero-background" aria-hidden="true">
        {background && (
          <RecoverableImage
            src={background}
            alt=""
            onFailureChange={(failed) => setFailedBackground(failed ? background : null)}
          />
        )}
        <svg width="100%" height="100%" viewBox="0 0 1 1" preserveAspectRatio="none">
          <defs>
            <linearGradient id={`${id}-fallback`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={colors.surfaceMuted} stopOpacity=".92" />
              <stop offset=".58" stopColor={colors.surfaceSoft} stopOpacity=".7" />
              <stop offset=".82" stopColor={colors.background} stopOpacity=".72" />
              <stop offset="1" stopColor={colors.background} />
            </linearGradient>
            <linearGradient id={`${id}-scrim`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#000" stopOpacity=".56" />
              <stop offset=".56" stopColor="#000" stopOpacity=".46" />
              <stop offset=".76" stopColor="#000" stopOpacity=".34" />
              <stop offset="1" stopColor="#000" stopOpacity=".18" />
            </linearGradient>
            <linearGradient id={`${id}-fade`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={colors.background} stopOpacity="0" />
              <stop offset=".62" stopColor={colors.background} stopOpacity="0" />
              <stop offset=".74" stopColor={colors.background} stopOpacity=".12" />
              <stop offset=".90" stopColor={colors.background} stopOpacity=".76" />
              <stop offset="1" stopColor={colors.background} />
            </linearGradient>
          </defs>
          <rect width="1" height="1" fill={`url(#${id}-${hasBackground ? 'scrim' : 'fallback'})`} />
          {hasBackground && <rect width="1" height="1" fill={`url(#${id}-fade)`} />}
        </svg>
      </div>
      <div className={`pp-my-topbar${onBack ? ' has-back' : ''}`}>
        {editing && (
          <strong className="pp-my-edit-title">{en ? 'Edit Profile' : '프로필 수정'}</strong>
        )}
        {(onBack || onSettings) && (
          <button
            type="button"
            aria-label={onBack ? (en ? 'Back' : '뒤로가기') : en ? 'Settings' : '설정'}
            disabled={editing && busy}
            data-profile-focus-key={onSettings && !onBack ? 'my-settings' : undefined}
            onClick={onBack || onSettings}
          >
            {onBack ? <FiArrowLeft size={23} /> : <FiSettings size={21} />}
          </button>
        )}
        {editing && (
          <button
            type="button"
            className="pp-my-edit-save"
            aria-label={en ? 'Save profile' : '프로필 저장'}
            disabled={busy}
            onClick={onSave}
          >
            <FiCheck size={22} />
          </button>
        )}
      </div>
      <div className="pp-my-profile-content">
        <div className="pp-my-profile-row">
          <div className="pp-my-avatar-wrap">
            <div className="pp-my-avatar" aria-hidden="true">
              {image ? (
                <RecoverableImage
                  src={image}
                  alt=""
                  fallback={nickname.slice(0, 1).toUpperCase()}
                />
              ) : (
                nickname.slice(0, 1).toUpperCase()
              )}
            </div>
            {editing && (
              <button
                type="button"
                className="pp-my-edit-photo"
                aria-label={en ? 'Change or remove profile photo' : '프로필 사진 변경 또는 삭제'}
                onClick={onProfileImage}
                disabled={busy}
              >
                <FiCamera size={15} />
              </button>
            )}
          </div>
          <div className="pp-my-profile-copy">
            <h1
              style={{
                color: /^#[\da-f]{3,8}$/i.test(nicknameColor || '') ? nicknameColor : undefined,
              }}
            >
              <span>{nickname}</span>
              <ProfileMark mark={myProfileMark(profile)} />
            </h1>
            <div className="pp-my-follow-row">
              <button type="button" disabled={editing} onClick={onFollowers}>
                {en ? 'Followers' : '팔로워'} {profile.followersCount ?? 0}
              </button>
              <span aria-hidden="true" />
              <button type="button" disabled={editing} onClick={onFollowing}>
                {en ? 'Following' : '팔로잉'} {profile.followingCount ?? 0}
              </button>
            </div>
          </div>
        </div>
        {profile.bio ? (
          <p className="pp-my-bio">{profile.bio}</p>
        ) : onEdit ? (
          <button
            type="button"
            className="pp-my-bio is-empty"
            data-profile-focus-key="my-bio-edit"
            onClick={onEdit}
          >
            {emptyBio}
          </button>
        ) : (
          <p className="pp-my-bio is-empty">{emptyBio}</p>
        )}
        {editing ? (
          <div className="pp-my-edit-background">
            <button type="button" onClick={onBackgroundImage} disabled={busy}>
              <FiCamera size={16} aria-hidden="true" />
              <span>{en ? 'Change Background' : '배경 변경'}</span>
            </button>
          </div>
        ) : (
          <div className="pp-my-actions">
            {onEdit ? (
              <button type="button" data-profile-focus-key="my-edit" onClick={onEdit}>
                <FiEdit2 size={16} />
                <span>{en ? 'Edit Profile' : '프로필 수정'}</span>
              </button>
            ) : primaryAction ? (
              <button
                type="button"
                className={`pp-profile-follow ${primaryAction.active ? 'is-following' : 'is-new'}`}
                disabled={primaryAction.disabled}
                aria-pressed={!!primaryAction.active}
                onClick={primaryAction.onPress}
              >
                {primaryAction.active ? <FiUserCheck size={16} /> : <FiUserPlus size={16} />}
                <span>{primaryAction.label}</span>
              </button>
            ) : null}
            <button type="button" onClick={onShare} disabled={shareDisabled}>
              <FiShare size={18} />
              <span>{en ? 'Share' : '공유'}</span>
            </button>
            {onFindUsers && (
              <button
                type="button"
                className="pp-my-find-members"
                aria-label={en ? 'Find members to follow' : '팔로우할 회원 찾기'}
                onClick={onFindUsers}
              >
                <FiUserPlus size={17} />
              </button>
            )}
            {onSafety && (
              <button
                type="button"
                className="pp-profile-safety"
                aria-label={en ? 'Report or Block' : '신고·차단'}
                onClick={onSafety}
              >
                <FiMoreHorizontal size={19} />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
